import React, { useState, useCallback } from 'react';
import { TailwindWrapper } from '@utils/TailwindWrapper';
import { cn } from '@utils/cn';
import ProfileSettings from './ProfileSettings';
import SubscriptionSettings from './SubscriptionSettings';
import EmailSettings from './EmailSettings';
import { Text, TwButton as Button, Icon } from '@ui-component-library';
import { UserSettingsProvider } from './UserSettingsContext';

const TABS = [
  { id: 'profile', label: 'Profil och konto', component: ProfileSettings, icon: 'UserCircleSimple', description: 'Ändra kontaktuppgifter så att vi kan nå dig.' },
  { id: 'subscriptions', label: 'Prenumerationer', component: SubscriptionSettings, icon: 'ChartLineSimple', description: 'Se och hantera prenumerationer' },
  { id: 'email', label: 'E-postinställningar', component: EmailSettings, icon: 'CommentBoxSimple', description: 'Välj vilka mail du vill ha från oss.' },
] as const;

type TabId = (typeof TABS)[number]['id'];
type TabConfig = (typeof TABS)[number];


function MobileTabItem({ tab, onSelect }: { tab: TabConfig; onSelect: () => void }) {
  return (
    <button
      type="button"
      className="flex items-center justify-between w-full px-4 py-2 cursor-pointer hover:bg-cloud-100 rounded-lg text-left"
      onClick={onSelect}
    >
      <div className="flex flex-col">
        <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">{tab.label}</Text>
        <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">{tab.description}</Text>
      </div>
      <Icon tw name="ArrowShortSimple" size="small" className="rotate-180 shrink-0" />
    </button>
  );
}

export default function UserSettings(): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [showMobileContent, setShowMobileContent] = useState(false);

  const ActiveTabComponent = TABS.find((t) => t.id === activeTab)?.component;

  const handleTabSelect = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    setShowMobileContent(true);
  }, []);

  const handleBackToTabs = useCallback(() => setShowMobileContent(false), []);

  return (
    <UserSettingsProvider>
    <TailwindWrapper>
      <div className="w-full max-w-full px-0">
        {!showMobileContent && (
          <header className={cn("flex-shrink-0 p-4 border-b border-cloud-500 flex flex-col lg:bg-cloud-200 bg-white-50",)}>
            <Button variant="outline" type="button" className="lg:hidden self-start mb-2-5 rounded-md" size="icon" onClick={() => { window.history.back(); }} aria-label="Gå bakåt">
              <Icon tw name="ArrowShortSimple" size="large" />
            </Button>
            <Text variant="text-xl/leading-normal/semibold" className="text-charcoal-1000">
              Inställningar
            </Text>
            <Text variant="text-sm/leading-normal/normal" className="text-[#737373] mt-0.5">
              Hantera din profil och prenumerationer
            </Text>
          </header>
        )}
        {/* Mobile navigation goes here*/}
        <div className="flex flex-col lg:flex-row h-full min-h-0 overflow-hidden">
          {!showMobileContent && (
            <nav className="w-full flex lg:hidden border-b border-cloud-500 flex-col" aria-label="Inställningsflikar">
              {TABS.map((tab) => (
                <MobileTabItem key={tab.id} tab={tab} onSelect={() => handleTabSelect(tab.id as TabId)} />
              ))}
            </nav>
          )}
          {/* Desktop navigation and content goes here*/}
          <nav className="hidden lg:flex flex-shrink-0 w-64  border-r border-cloud-500 flex-col p-2 sticky top-0 self-start h-screen" aria-label="Inställningsflikar">
            <div className="flex flex-col gap-0.5 mt-2">
              {TABS.map((tab) => (
                <Button
                  key={tab.id}
                  variant="tertiary"
                  fullWidth
                  size="small"
                  className={cn('justify-start rounded-lg py-3', activeTab === tab.id && 'bg-cloud-200')}
                  onClick={() => setActiveTab(tab.id as TabId)}
                >
                  <Icon tw name={tab.icon} size="small" />
                  {tab.label}
                </Button>
              ))}
            </div>
          </nav>
          {/* Main content goes here*/}
          <main
            className={cn(
              'flex-1 min-h-0 overflow-auto pb-2 flex flex-col bg-cloud-200',
              !showMobileContent && 'hidden lg:flex'
            )}
          >
            {showMobileContent && (
              <div className="relative flex items-center justify-center py-4">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={handleBackToTabs}
                  className="lg:hidden absolute left-0"
                  ariaLabel="Tillbaka"
                >
                  <Icon tw name="ArrowShortSimple" size="large" />
                </Button>
                <Text variant="text-base/leading-normal/medium" className="text-charcoal-1000">
                  {TABS.find((t) => t.id === activeTab)?.label}
                </Text>
              </div>
            )}
            {ActiveTabComponent && <ActiveTabComponent />}
          </main>
        </div>
      </div>
    </TailwindWrapper>
    </UserSettingsProvider>
  );
}